import React, { useState, useEffect } from "react";
import { Wand2, Loader2, Image as ImageIcon, Download } from "lucide-react";
import { generateImage, getImages, API } from "../../lib/api";
import { SectionHeader } from "./Sections";

const imgSrc = (u) => (u ? `${API.replace(/\/api$/, "")}${u}` : "");

const IDEAS = [
  "A cinematic hero image for a steel & infrastructure company annual report, gold and obsidian tones",
  "A clean executive presentation cover: 'US Market Entry Strategy', minimalist premium",
  "An elegant infographic-style illustration of a 5-year transformation roadmap",
];

export default function CreativeStudio() {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => { getImages().then(setImages).catch(() => {}); }, []);

  const run = async (p) => {
    const text = (p || prompt).trim();
    if (!text || loading) return;
    setLoading(true);
    setError("");
    try {
      const res = await generateImage(text);
      setImages((prev) => [{ id: res.id, image_url: res.image_url, prompt: text, caption: res.caption }, ...prev]);
      setPrompt("");
    } catch (e) {
      setError("Image generation failed. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div>
      <SectionHeader overline="Communications & Creative Studio" title="Creative Studio"
        desc="Production-quality visuals for decks, campaigns and executive communications — powered by Nano Banana." />

      <div className="glass-card p-5 mb-6 fade-up">
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={3}
          placeholder="Describe the image, deck cover, or infographic you need…"
          className="w-full bg-[#0F131C] border border-white/10 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:border-amber-500/50"
          data-testid="creative-prompt"
        />
        <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
          <div className="flex flex-wrap gap-2">
            {IDEAS.map((s) => (
              <button key={s} onClick={() => setPrompt(s)} className="text-[11px] px-2.5 py-1 rounded-full border border-white/10 text-slate-400 hover:border-amber-500/40 transition max-w-[220px] truncate">
                {s}
              </button>
            ))}
          </div>
          <button onClick={() => run()} disabled={loading || !prompt.trim()} className="gold-btn flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm disabled:opacity-40" data-testid="creative-generate">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />} Generate
          </button>
        </div>
        {error && <div className="text-rose-400 text-sm mt-2">{error}</div>}
      </div>

      {loading && (
        <div className="glass-card p-10 mb-6 grid place-items-center fade-up">
          <div className="flex items-center gap-2 text-slate-400"><Loader2 className="animate-spin" size={18} /> Rendering your visual…</div>
        </div>
      )}

      {images.length === 0 && !loading ? (
        <div className="glass-card p-12 text-center text-slate-500">
          <ImageIcon size={32} className="mx-auto mb-3 opacity-50" />
          Your generated visuals will appear here.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {images.map((img) => (
            <div key={img.id} className="glass-card overflow-hidden fade-up group" data-testid="creative-image">
              <div className="relative">
                <img src={imgSrc(img.image_url)} alt={img.prompt} className="w-full aspect-square object-cover" />
                <a href={imgSrc(img.image_url)} download={`office-${img.id}.png`}
                  className="absolute top-2 right-2 p-2 rounded-lg bg-black/60 opacity-0 group-hover:opacity-100 transition" title="Download">
                  <Download size={15} />
                </a>
              </div>
              <div className="p-3 text-[11px] text-slate-400 line-clamp-2">{img.prompt}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
